import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  Input,
  Space,
  Typography,
  Row,
  Col,
  Skeleton,
  ConfigProvider,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";

import { showVisumEffect } from "../../components/utils/antWaveEffect";
import { toast } from "../../components/toasts/toastStore";
import { apiLentes, authHeader } from "../../redux/actions/catalogos/catalogosApi";
import { BannerFoto } from "./EntidadFotoCard";
import "./PaquetesPage.css";
import { usePermisos } from "../../context/PermisosContext";

const { Title, Text, Paragraph } = Typography;

const BANNER_BG = "linear-gradient(135deg, var(--brand-dark, #3d5590) 0%, var(--brand-primary) 100%)";

function stockBadge(item) {
  if (!item.maneja_inventario) {
    return { cls: "paq-tipo-neutral", label: "Sin inventario" };
  }
  const stock = item.stock_actual;
  if (stock === undefined || stock === null) {
    return { cls: "paq-tipo-neutral", label: "Stock: —" };
  }
  if (stock <= 0) return { cls: "paq-tipo-peligro", label: `Stock: ${stock}` };
  if (stock < 5) return { cls: "paq-tipo-barra", label: `Stock: ${stock}` };
  return { cls: "paq-tipo-decoracion", label: `Stock: ${stock}` };
}

export default function LentesPage() {
  const navigate = useNavigate();
  const { perm } = usePermisos() || { perm: () => true };

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [activeFilter, setActiveFilter] = useState("todos");

  const lastFetchKey = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const fetchLentes = () => {
    setLoading(true);
    apiLentes
      .get("/lentes", { headers: authHeader(), params: { search: searchDebounced || undefined } })
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : res.data?.items ?? [];
        setItems(data);
      })
      .catch(() => toast.error("No se pudo cargar el catalogo de lentes"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const key = searchDebounced;
    if (lastFetchKey.current === key) return;
    lastFetchKey.current = key;
    fetchLentes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDebounced]);

  const counts = useMemo(
    () => ({
      total: items.length,
      conInventario: items.filter((i) => Boolean(i.maneja_inventario)).length,
      inactivos: items.filter((i) => !i.active).length,
    }),
    [items]
  );

  const filteredItems = useMemo(() => {
    if (activeFilter === "con_inventario") return items.filter((i) => Boolean(i.maneja_inventario));
    if (activeFilter === "inactivos") return items.filter((i) => !i.active);
    return items;
  }, [items, activeFilter]);

  const STAT_CARDS = [
    { key: "todos", label: "Total de modelos", icon: <i className="fa-solid fa-glasses" />, iconCls: "paq-stat-icon-primario", count: counts.total },
    { key: "con_inventario", label: "Con inventario", icon: <i className="fa-solid fa-boxes-stacked" />, iconCls: "paq-stat-icon-correo", count: counts.conInventario },
    { key: "inactivos", label: "Inactivos", icon: <i className="fa-solid fa-ban" />, iconCls: "paq-stat-icon-menores", count: counts.inactivos },
  ];

  return (
    <ConfigProvider wave={{ showEffect: showVisumEffect }}>
      <main className="paq-main">
        <div className="paq-content">
          <section className="paq-section">
            <div className="paq-filters-panel">
              <Space direction="vertical" size={2} style={{ marginBottom: 16 }}>
                <Title level={2} className="paq-title">Lentes</Title>
                <Text className="paq-subtitle">Catalogo de armazones y lentes, con su kardex de entradas y salidas de inventario.</Text>
              </Space>

              <Row gutter={[16, 14]}>
                <Col xs={24}>
                  <div className="paq-field-label">Buscador</div>
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar por modelo o proveedor..."
                    suffix={<SearchOutlined className="paq-input-suffix" />}
                    className="paq-control"
                    allowClear
                  />
                </Col>
              </Row>

              <Row gutter={[16, 14]} style={{ marginTop: 14 }} align="bottom">
                <Col xs={24} lg={6}>
                  <div className="paq-actions">
                    <Button
                      className="paq-btn-clean"
                      onClick={() => {
                        setSearch("");
                        setActiveFilter("todos");
                      }}
                    >
                      Limpiar filtros
                    </Button>
                  </div>
                </Col>
              </Row>
            </div>

            <div className="paq-stats-row" style={{ gridTemplateColumns: `repeat(${STAT_CARDS.length}, minmax(0, 1fr))` }}>
              {STAT_CARDS.map(({ key, label, icon, iconCls, count }) => (
                <Card
                  key={key}
                  className={`paq-stat-card ${activeFilter === key ? "paq-stat-active" : ""}`}
                  hoverable
                  onClick={() => setActiveFilter((p) => (p === key ? "todos" : key))}
                >
                  <Space align="center" size={10}>
                    <div className={`paq-stat-icon ${iconCls}`}>{icon}</div>
                    <div>
                      <div className="paq-stat-value">{count}</div>
                      <div className="paq-stat-label">{label}</div>
                    </div>
                  </Space>
                </Card>
              ))}
            </div>

            <div className="paq-expedientes-card">
              <div className="paq-toolbar">
                <div className="paq-toolbar-left">
                  <Title level={4} style={{ marginBottom: 0 }}>Lentes ({filteredItems.length})</Title>
                  <Text type="secondary">{filteredItems.length} modelos encontrados</Text>
                </div>
                <div className="paq-toolbar-right">
                  {perm("lentes", "insertar") && (
                    <Button
                    type="primary"
                    icon={<i className="fa-solid fa-plus" />}
                    onClick={() => navigate("/app/lentes/crear")}
                    style={{ backgroundColor: "var(--brand-primary)", borderColor: "var(--brand-primary)" }}
                  >
                    Nuevo lente
                  </Button>
                  )}
                </div>
              </div>

              {!loading && filteredItems.length === 0 && (
                <div className="paq-empty">
                  <div style={{ fontSize: 16, fontWeight: 500 }}>Sin lentes que coincidan con los filtros</div>
                </div>
              )}

              <div className="paq-grid">
                {loading &&
                  Array.from({ length: 8 }).map((_, i) => (
                    <div key={`sk-${i}`} className="paq-card paq-card-skeleton">
                      <div className="paq-card-header">
                        <Skeleton active title={{ width: "60%" }} paragraph={{ rows: 1, width: "40%" }} />
                      </div>
                      <div className="paq-card-body">
                        <Skeleton active title={false} paragraph={{ rows: 3 }} />
                      </div>
                    </div>
                  ))}

                {!loading &&
                  filteredItems.map((lente) => {
                    const badge = stockBadge(lente);
                    return (
                      <div key={lente.id_lente_modelo} className="paq-card">
                        <div className="paq-card-banner" style={{ background: BANNER_BG }}>
                          <BannerFoto item={lente} icon="fa-glasses" />
                        </div>

                        <div className="paq-card-header">
                          <div className="paq-card-head">
                            <Paragraph className="paq-card-name" ellipsis={{ rows: 2 }}>{lente.nombre_modelo}</Paragraph>
                            <span className={`paq-tipo-badge ${badge.cls}`}>{badge.label}</span>
                          </div>
                        </div>

                        <div className="paq-card-body">
                          <div className="paq-contenidos-box">
                            <i className="fa-solid fa-truck-field paq-info-icon" />
                            <div className="paq-info-text">
                              <span className="paq-info-label">Proveedor</span>
                              <span className="paq-info-value">{lente.proveedor || "No especificado"}</span>
                            </div>
                          </div>
                          <div className="paq-contenidos-box">
                            <i className="fa-solid fa-circle-dot paq-info-icon" />
                            <div className="paq-info-text">
                              <span className="paq-info-label">Poder</span>
                              <span className="paq-info-value">{lente.poder || "No especificado"}</span>
                            </div>
                          </div>
                        </div>

                        <div className="paq-card-footer">
                          <Button
                            className="paq-btn-details"
                            icon={<i className="fa-solid fa-circle-info" />}
                            onClick={() => navigate(`/app/lentes/${lente.id_lente_modelo}`)}
                          >
                            Ver detalles
                          </Button>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          </section>
        </div>
      </main>

    </ConfigProvider>
  );
}
